import apiService from '../api/api.service.js';

// Shared logic for images in the LoanLaunch chat (LoanChatDrawer + ConsumerPortalView).
// Messages themselves live in SWIFT's WebAPI (which we can't change); the images live in
// ConsumerAPI's MessageAttachments table, keyed by the message id they belong to.

export const IMAGE_PLACEHOLDER = '[Image]';
export const MAX_IMAGES = 5;
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
const ALLOWED_EXT = /\.(jpe?g|png|gif|webp|heic|heif)$/i;

// A presigned URL lives ~15 minutes; keep the one we already have (so polling doesn't
// reload every <img>) until it is this old, then take the fresh one.
const URL_REUSE_MS = 10 * 60 * 1000;

const LOG = '[ChatImages]';

let enabledPromise = null;

/** Whether the feature is switched on server-side. Any failure counts as off. */
export function isChatImagesEnabled(token) {
  if (!enabledPromise) {
    enabledPromise = apiService.GetMessageAttachmentsEnabled(token)
      .then(res => res?.data?.isSuccess !== false && res?.data?.data === true)
      .catch(error => {
        console.warn(LOG, 'Enabled check failed; image chat disabled.', error);
        enabledPromise = null; // allow a retry on the next boot
        return false;
      });
  }
  return enabledPromise;
}

export function newClientId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function idKey(id) {
  return id == null ? '' : String(id).toLowerCase();
}

export function isImageFile(file) {
  if (!file) return false;
  if (file.type) return ALLOWED_TYPES.includes(file.type.toLowerCase());
  return ALLOWED_EXT.test(file.name || ''); // HEIC often arrives with an empty type
}

/**
 * Splits candidate files into the ones we can stage and a list of human-readable problems.
 * Client-side only — the server re-validates everything.
 */
export function validateImages(files, alreadyStaged = 0) {
  const accepted = [];
  const errors = [];
  for (const file of files) {
    if (!isImageFile(file)) {
      errors.push(`${file.name || 'File'} is not a supported image (JPEG, PNG, GIF, WEBP, HEIC).`);
    } else if (file.size > MAX_IMAGE_BYTES) {
      errors.push(`${file.name || 'Image'} is larger than 10 MB.`);
    } else if (alreadyStaged + accepted.length >= MAX_IMAGES) {
      errors.push(`You can attach up to ${MAX_IMAGES} images per message.`);
      break;
    } else {
      accepted.push(file);
    }
  }
  return { accepted, errors };
}

/**
 * Fetches a loan's attachments and groups them by lower-cased message id.
 * `previous` is the map from the last fetch; still-fresh URLs are carried over.
 */
export async function fetchAttachmentsByMessage(loanId, token, previous = {}) {
  const res = await apiService.GetMessageAttachmentsByLoanId(loanId, token);
  const rows = res?.data?.data || [];
  const now = Date.now();
  const prevById = {};
  Object.values(previous).flat().forEach(a => { prevById[idKey(a.id)] = a; });

  const map = {};
  rows.forEach(row => {
    const prev = prevById[idKey(row.id)];
    const item = prev && prev.url && now - prev._fetchedAt < URL_REUSE_MS
      ? { ...row, url: prev.url, _fetchedAt: prev._fetchedAt }
      : { ...row, _fetchedAt: now };
    const key = idKey(row.messageId);
    (map[key] = map[key] || []).push(item);
  });
  Object.values(map).forEach(list => list.sort((a, b) => new Date(a.createdUtc) - new Date(b.createdUtc)));
  return map;
}

/** Text to show for a message; hides the "[Image]" placeholder when the images are there. */
export function displayMessageText(message, attachmentsByMessage) {
  const text = message?.messageText ?? '';
  if (text.trim() === IMAGE_PLACEHOLDER && (attachmentsByMessage?.[idKey(message.id)] || []).length) {
    return '';
  }
  return text;
}

function errorMessage(error, fallback) {
  const data = error?.response?.data;
  if (typeof data === 'string' && data) return data;
  return data?.message || error?.message || fallback;
}

/**
 * Sends a chat message, uploading `files` first when there are any.
 * `request` is the {message, notiDto} body both views already build for UpsertMessage;
 * with no files it is sent exactly as before.
 *
 * Flow with images:
 *  1. upload under a client-generated GUID,
 *  2. UpsertMessage with message.id = that GUID (SWIFT may or may not keep it),
 *  3. if SWIFT assigned its own id, Relink the attachments to it.
 *
 * Throws an Error with a user-facing message; `error.stage` is 'upload' or 'send'.
 * Returns the UpsertMessage response (whose data.data is the full message list).
 */
/** Suffix for notification text so recipients know a message with text also has images. */
function imageNote(count) {
  return `(${count} image${count === 1 ? '' : 's'} attached)`;
}

export async function sendChatMessage({ request, files = [], token }) {
  if (!files.length) {
    return apiService.UpsertMessage(request, token);
  }

  const clientId = newClientId();
  const loanId = request.message.loanId;
  const userName = request.message.userName;
  const text = (request.message.messageText || '').trim() || IMAGE_PLACEHOLDER;

  request.message.id = clientId;
  request.message.messageText = text;
  // Image-only messages keep the bare placeholder, which GSWebAPIv2 swaps for readable text.
  if (request.notiDto) request.notiDto.message = text === IMAGE_PLACEHOLDER ? text : `${text} ${imageNote(files.length)}`;

  const form = new FormData();
  form.append('loanId', loanId);
  form.append('messageId', clientId);
  form.append('uploadedBy', userName || '');
  files.forEach(f => form.append('files', f, f.name));

  try {
    const up = await apiService.UploadMessageAttachments(form, token);
    if (up?.data?.isSuccess === false) {
      throw new Error(up.data.message || 'Image upload failed.');
    }
  } catch (error) {
    const err = new Error(errorMessage(error, 'Image upload failed.'));
    err.stage = 'upload';
    err.cause = error;
    throw err;
  }

  let res;
  try {
    res = await apiService.UpsertMessage(request, token);
  } catch (error) {
    const err = new Error(errorMessage(error, 'Failed to post message.'));
    err.stage = 'send';
    err.cause = error;
    throw err;
  }

  const list = res?.data?.data || [];
  if (list.some(m => idKey(m.id) === idKey(clientId))) {
    console.info(LOG, 'SWIFT kept the client message id; no relink needed.', clientId);
    return res;
  }

  const match = list
    .filter(m => m.userName === userName && (m.messageText || '').trim() === text)
    .sort((a, b) => new Date(b.sent) - new Date(a.sent))[0];

  if (!match) {
    console.warn(LOG, 'SWIFT replaced the client id but the sent message was not found; attachments left on', clientId);
    return res;
  }

  try {
    const rel = await apiService.RelinkMessageAttachments({ loanId, oldMessageId: clientId, newMessageId: match.id }, token);
    console.info(LOG, 'SWIFT assigned its own message id; relinked attachments.', { from: clientId, to: match.id, rows: rel?.data?.data });
  } catch (error) {
    // The message went out; only its images are orphaned. Don't fail the send for it.
    console.error(LOG, 'Relink failed; attachments left on', clientId, error);
  }
  return res;
}
